import React, { useMemo } from "react";
import { Container, Col, Row, Alert } from "reactstrap";
import { BreadcrumbItem } from "reactstrap";
import { Link } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { Component as Form } from "@repoflow/form-schema";
import * as Notify from "@repoflow/form-schema/notify";
import {
  Mutation,
  Button,
  useSectionContext,
} from "@repoflow/components/section";
import { CancellableContent } from "@repoflow/components/content-cancel";
import { translatedRoute } from "@common/schema/site/locale";
import * as widgets from "widgets/register";
import * as PageQL from "queries/page";
import * as SiteQL from "queries/site";

import { translateLocaleOptions } from "../edit/locales";
import { GROUP_SCAN, Translations, groupMembers } from "../translations";

export const Sidebar = () => null;

export const Indice = () => <BreadcrumbItem>Translate</BreadcrumbItem>;

// The fields, and the one that is a picker.
//
// A function rather than a constant because the options are the site's, and
// module scope for what it returns is not available to us — so every caller
// memoises it on the option list. A fresh `config` identity rebuilds the widget
// tree beneath the form's SchemaProvider and detaches the control being typed
// into (P139), and `route` and `title` here are both typed into.
//
// Exported so the suite drives the field list the screen renders rather than a
// copy of it, the same way the Page tab's `pageFields` is.
export const translateFields = (localeOptionsFn) => ({
  "/Public/Page/Translate": {
    fields: [
      // A PICKER, never a text box. The valid values are written down on
      // another screen — Locales on the site's Translations tab — and `translate`
      // refuses anything else with `page.translate.locale.undeclared`, so a
      // free-text field here is one whose every wrong answer is a round trip
      // to find out. The Page tab made the same call for the same field.
      { property: "locale", label: "Into which language", widget: "select", options: localeOptionsFn },
      // Both left empty in the common case. The server derives `/es/pricing`
      // from `/pricing` and this site's declared prefixes; an author who wants
      // `/es/precios` — a slug in the target language, which is most of the
      // reason to translate at all — types it here. What they would get by
      // leaving it alone is on the screen above the form, so leaving it alone
      // is a decision rather than a leap.
      { property: "route", label: "Route" },
      { property: "title", label: "Title" },
    ],
  },
});

// What the form is seeded with.
//
// Exported because it is half of what decides whether the Translate button is
// pressable: a form is validated as a WHOLE value object, so it is THIS shape
// the schema has to accept and not the minimal one a test would reach for. The
// suite beside this file validates the exported object rather than a copy —
// a copy is what let `title: ""` sit here failing the shared field's
// `minLength: 1` for the life of the screen, with the button grey and nothing
// on it saying why. See `translateTitle` in @common/schema/page/fields.
export const INITIAL = { locale: "", route: "", title: "" };

// What the form's values mean on the wire.
//
// The empty fields are DROPPED rather than sent blank, and that is the whole of
// this function. `PageModel.translate` reads an ABSENT `route` as "derive it
// from the site's prefixes" and an absent `title` as "copy the source's" — an
// empty STRING is neither: it is a value, and it would produce a page with no
// title on a route of "", which no URL can ever match (`canonicalRoute` refuses
// the empty string precisely because it is `route`'s own default).
//
// Exported so the suite drives the same function the screen submits through.
export const translateInput = (values = {}) => ({
  locale: values.locale,
  ...(values.route ? { route: values.route } : {}),
  ...(values.title ? { title: values.title } : {}),
});

// The languages this page can still be translated into.
//
// Two exclusions, and both of them are refusals in the model rather than taste:
// the source's own language is `page.translate.locale.same`, and a language the
// group already has a page in is `page.translate.locale.exists`. A picker that
// offered either would be offering an author a round trip to an error.
//
// The source's language is `page.locale` when it has one and the site's
// `defaultLocale` when it does not — which is the resolution `localeOf` in
// @common/render/page makes and the one `translate` itself compares against, so
// an untranslated page on a site whose default is `en` does not offer `en`.
export const availableLocales = (site, page, members) => {
  const locales = (site && site.locales) || [];
  const source = (page && page.locale) || (site && site.defaultLocale) || "";
  const taken = new Set((members || []).map((member) => member.locale || ""));

  return locales.filter(
    (locale) => locale && locale.code !== source && !taken.has(locale.code)
  );
};

// What the author is about to create, as they are about to create it.
//
// The point of the two empty fields is that the server fills them in, and the
// cost of that has always been that the author cannot see what it will fill
// them in WITH until the page exists. The route is the one that matters: it is
// derived from prefixes declared on a different screen, it is what the page is
// reachable at, and `page.translate.route.taken` is the only way anyone found
// out it was not what they assumed.
//
// `translatedRoute` is imported rather than reimplemented — it is the function
// the model calls, and @common/schema/site/locale says why it lives there.
//
// Returns null until a language is picked, because until then there is nothing
// to derive from and a preview of the source's own route would be a lie.
export const translationPreview = (site, page, values = {}) => {
  if (!values.locale) {
    return null;
  }

  return {
    locale: values.locale,
    route: values.route || translatedRoute(site, page, values.locale),
    routeDerived: !values.route,
    title: values.title || (page && page.title) || "",
    titleCopied: !values.title,
  };
};

// The model's refusals, in a sentence each.
//
// Every one of these is a state the author can reach from this screen, and
// every one of them arrives as a bare code inside a GraphQL error string —
// `page.translate.route.taken:/es/pricing`. Printing that was accurate and
// useless: it names no field, suggests no fix, and reads as a crash.
//
// The code is matched as a SUBSTRING because what wraps it is not ours: Apollo
// prefixes "ApolloError: " or "Error: GraphQL error: " depending on the link
// chain, and the model appends the offending value after a colon. Anything
// unrecognised falls through to the original text rather than to a generic
// apology — an unknown failure the author can quote is worth more than a
// friendly sentence that loses it.
export const translateError = (error) => {
  const text = (error && error.toString()) || "";
  const detail = (text.split(/page\.translate\.[a-z.]+:/)[1] || "").trim();

  if (text.includes("page.translate.route.taken")) {
    return `Another page of this site is already on ${detail || "that route"}. Give this translation a route of its own.`;
  }

  if (text.includes("page.translate.locale.exists")) {
    return `This page already has a ${detail || "translation in that language"} translation. Open it instead of creating a second one.`;
  }

  if (text.includes("page.translate.locale.same")) {
    return `This page is already written in ${detail || "that language"}.`;
  }

  if (text.includes("page.translate.locale.undeclared")) {
    return `This site does not declare ${detail || "that language"}. Add it under Locales on the site's Translations tab first.`;
  }

  if (text.includes("page.translate.invalid")) {
    return "That is not a route this service can store. A route starts with a slash.";
  }

  return text;
};

export const Content = ({
  history,
  match: {
    params,
    params: { siteId, pageId },
  },
}) => {
  const { values: page } = useSectionContext();

  // cache-first, so this is a cache read rather than a round trip — the screens
  // above already resolved this site. It is here for one thing: the languages
  // the site actually declares, so the author is shown what the server will
  // accept instead of guessing a code and being refused by
  // `page.translate.locale.undeclared`.
  const { data } = useQuery(SiteQL.get, {
    variables: { siteId },
    fetchPolicy: "cache-first",
  });

  // The site's pages, and only when this one is in a translation group. See
  // GROUP_SCAN for why one listing page is enough for what this is used for,
  // and `groupMembers` for why a page with no key asks nothing.
  const { data: pages } = useQuery(PageQL.list, {
    variables: { siteId, input: { limit: GROUP_SCAN } },
    fetchPolicy: "cache-first",
    skip: !(page && page.translationKey),
  });

  const site = data && data.viewer.account.sites.get;

  // The unwrap is INSIDE the memo, not above it: a `||` on the outside is a
  // fresh array identity on every render, which is a dependency that always
  // differs and a memo that never holds — and it would carry the two below it
  // with it, rebuilding the form config and detaching the control being typed
  // into (P139).
  const members = useMemo(
    () =>
      groupMembers(
        (pages && pages.viewer.account.sites.get.pages.list.items) || [],
        page
      ),
    [pages, page]
  );
  const available = useMemo(
    () => availableLocales(site, page, members),
    [site, page, members]
  );

  // Memoised on the option list for the reason `translateFields` gives: a fresh
  // config identity detaches the control being typed into (P139).
  const localeOptionsFn = useMemo(
    () => translateLocaleOptions(available),
    [available]
  );
  const fields = useMemo(() => translateFields(localeOptionsFn), [localeOptionsFn]);

  const locales = (site && site.locales) || [];

  // Two dead ends, and they are different dead ends. A site with no languages
  // is a site where nobody has done the setup yet; a site whose languages are
  // all used up is a page that is already fully translated. Both used to be a
  // form with a picker that could not be satisfied — form-schema renders "No
  // options" and no control for an empty option list, which is a screen with a
  // Translate button and nothing to press it about.
  if (locales.length === 0 || available.length === 0) {
    return (
      <CancellableContent>
        <Container className="mt-4">
          <Row>
            <Col>
              {locales.length === 0 ? (
                <Alert color="info">
                  This site does not declare any languages yet. Add them under{" "}
                  <Link to={`/site/${siteId}`}>Locales on the site's Translations tab</Link>,
                  and this page can be translated into one of them.
                </Alert>
              ) : (
                <Alert color="info">
                  This page already exists in every language this site declares.
                </Alert>
              )}
            </Col>
          </Row>
          <Row>
            <Col>
              <Translations
                siteId={siteId}
                site={site}
                members={members}
                heading="Already translated"
              />
            </Col>
          </Row>
        </Container>
      </CancellableContent>
    );
  }

  return (
    <Mutation
      initial={INITIAL}
      target={{ $ref: "/Public/Page/Translate" }}
      mutation={PageQL.translate}
      update={PageQL.update(params)}
      onMutate={({ handler, state: { values } }) => {
        handler({ variables: { siteId, pageId, input: translateInput(values) } });
      }}
      onCompleted={(res) => {
        const created = res.viewer.account.sites.get.pages.get.translate;
        // Into the new page's editor. The copy is the thing the author came to
        // write, and leaving them on the source is a navigation they would make
        // every single time.
        history.replace(`/site/${siteId}/page/${created.pageId}/edit`);
      }}
      onError={(error) => {
        Notify.error(translateError(error));
      }}
    >
      {(props) => (
        <TranslateHandler
          {...props}
          site={site}
          page={page}
          members={members}
          siteId={siteId}
          fields={fields}
        />
      )}
    </Mutation>
  );
};

// What the author is about to get, beside the form that decides it.
//
// Live rather than on submit, and that is the point: the two fields under it
// are optional precisely because the server can fill them in, and this is the
// only place their filled-in value is visible before the page exists.
const Preview = ({ preview }) => {
  if (!preview) {
    return null;
  }

  return (
    <Alert color="light" className="border">
      <div>
        <b>Route</b> <code>{preview.route}</code>{" "}
        {preview.routeDerived && (
          <span className="text-muted">
            — derived from the site's prefix for {preview.locale}
          </span>
        )}
      </div>
      <div>
        <b>Title</b> {preview.title}{" "}
        {preview.titleCopied && (
          <span className="text-muted">— copied from this page</span>
        )}
      </div>
    </Alert>
  );
};

const TranslateHandler = ({
  target,
  state,
  schema,
  mutate,
  site,
  page,
  members,
  siteId,
  fields,
}) => {
  const { values, setValues } = state;
  const preview = translationPreview(site, page, values);

  return (
    <CancellableContent>
      <Container className="mt-4">
        <Row>
          <Col>
            <p>
              A copy of <b>{(page && page.title) || "this page"}</b> in another
              language. Its elements and its document overrides are copied as
              they are now; the two pages are independent from that moment on
              and are linked only by the translation group they share.
            </p>
          </Col>
        </Row>
        <Row>
          <Col>
            <Form
              schema={schema}
              target={target}
              widgets={widgets}
              config={fields}
              values={values}
              onChange={setValues}
              onSubmit={() => {
                mutate();
              }}
            >
              <Preview preview={preview} />
              <Button type="submit">Translate</Button>
            </Form>
          </Col>
        </Row>
        <Row>
          <Col>
            <Translations
              siteId={siteId}
              site={site}
              members={members}
              heading="Already translated"
            />
          </Col>
        </Row>
      </Container>
    </CancellableContent>
  );
};
